import { Home, ResidentItem } from '@lmc/ts-common'
import { Avatar, Heading } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { getNeedsNames, getResidentInfoDetail } from '../utils/utils'
import { Text } from '@lmc/ui-kit/src/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import Description from 'components/generic/LogTimelineRow/Description'
import { GeneralStatusModal } from './GeneralStatusModal'
import { ProfileBadges } from './ProfileBadges'
import { GeneralZoneSection } from './GeneralZoneSection'
import { GeneralNeedsSection } from './GeneralNeedsSection'
import { GeneralDNACPRSection } from './GeneralDNACPRSection'
import { EditTooltip } from './EditTooltip'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import requests from 'api'
import { ResidentFetchItemResponse } from '@lmc/validations'
import useFeatureFlags from 'hooks/useFeatureFlags'

type GeneralInformationProps = {
  resident: ResidentItem
  home: Home
  testId?: string
}

export const GeneralInformation = ({
  resident,
  home,
  testId
}: GeneralInformationProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const { id } = useParams()

  const flags = useFeatureFlags({
    flagNames: ['mods_demographics']
  })?.data

  const hasPro = home?.features?.pro?.enabled

  const isModsDemographicsEnabled = flags?.mods_demographics?.enabled

  const hasSkillsAndNeeds = home?.features?.skillsAndNeeds?.enabled

  const { data: residentNeedsData } = useQuery(
    ['needs', id] as string[],
    ({ queryKey }) =>
      requests.residents.needsList({ params: { id: queryKey[1] } }),
    {
      enabled: hasPro && Boolean(id)
    }
  )

  const hasNeeds = residentNeedsData && residentNeedsData?.result?.length > 0

  const { data: needsData } = useQuery(
    ['needsList'],
    () => requests.needs.list({ params: undefined }),
    {
      enabled: hasPro && hasSkillsAndNeeds && hasNeeds
    }
  )

  const residentNeeds = getNeedsNames(
    residentNeedsData?.result,
    needsData?.result
  )

  const hasSummarySection = !!resident?.summary

  return (
    <MainWrapper
      data-testid={testId ? `${testId}-general-info` : 'resident-general-info'}
    >
      <GeneralWrapper
        data-testid={
          testId ? `${testId}-general-wrapper` : 'resident-general-wrapper'
        }
      >
        <LeftWrapper
          data-testid={
            testId ? `${testId}-left-wrapper` : 'resident-left-wrapper'
          }
        >
          <AvatarWrapper
            data-testid={
              testId ? `${testId}-avatar-wrapper` : 'resident-avatar-wrapper'
            }
          >
            <Avatar
              url={resident?.picture as unknown as string}
              size={140}
              data-testid={testId ? `${testId}-avatar` : 'resident-avatar'}
            />
          </AvatarWrapper>
          <InfoWrapper
            data-testid={
              testId ? `${testId}-info-wrapper` : 'resident-info-wrapper'
            }
          >
            <Heading
              level={2}
              data-testid={testId ? `${testId}-name` : 'resident-name'}
            >
              {resident?.preferredName || resident?.name?.first}
            </Heading>
            <StyledText
              data-testid={testId ? `${testId}-details` : 'resident-details'}
            >
              {getResidentInfoDetail(resident as ResidentFetchItemResponse, isModsDemographicsEnabled)}
            </StyledText>
            <ProfileBadges resident={resident} testId={testId} />
          </InfoWrapper>
        </LeftWrapper>
        <GeneralStatusModal
          resident={resident as ResidentFetchItemResponse}
          testId={testId}
        />
      </GeneralWrapper>
      {hasPro && <GeneralDNACPRSection resident={resident} testId={testId} />}
      <GeneralZoneSection resident={resident} hasPro={hasPro} testId={testId} />
      {hasPro && hasNeeds && (
        <GeneralNeedsSection
          needsText={residentNeeds?.join(', ')}
          testId={testId}
        />
      )}
      {hasSummarySection && (
        <CoreSectionWrapper
          data-testid={
            testId ? `${testId}-summary-section` : 'resident-summary-section'
          }
        >
          <CoreSection
            data-testid={
              testId ? `${testId}-summary-content` : 'resident-summary-content'
            }
          >
            <StyledCoreLabel level={2}>
              <EditTooltip title={t(supConstants.PERSONAL_NOTES_TOOLTIP)}>
                {t(supConstants.PERSONAL_NOTES)}
              </EditTooltip>
            </StyledCoreLabel>
            <Description
              description={resident?.summary}
              data-testid={
                testId
                  ? `${testId}-summary-description`
                  : 'resident-summary-description'
              }
            />
          </CoreSection>
        </CoreSectionWrapper>
      )}
    </MainWrapper>
  )
}

const GeneralWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }
`

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    text-align: center;
    margin-top: 5px;
  }
`

const StyledCoreLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`

const CoreSectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
`

const CoreSection = styled.div`
  display: flex;
  flex-direction: column;
`

const AvatarWrapper = styled.div`
  width: 140px;
  height: 140px;
`

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.color.neutral.color11};
  padding: 24px;
  border-radius: ${(props) => props.theme.properties.borderRadius.large};
`
